/**
 * ControlRoomTopology — Connexis fixed-price routing simulator
 *
 *  Flow (left → right):
 *    Source (Our funnels / Affiliates / Publishers, rotating)
 *      └ packet ─→ Connexis Routing Engine (sector detection, glowing)
 *          └ packet ─→ Matched buyer (single emerald card, EXCLUSIVE, fixed CPL)
 *
 *  Strictly NO bidding language, NO auction, NO competing bids. Each lead
 *  flows in from one of three first-party source types and is routed to one
 *  exclusive buyer at the fixed sector CPL — under 100ms.
 */
import React, { useEffect, useRef, useState } from "react";
import {
  Activity, Play, Pause, RotateCcw, Volume2, VolumeX,
  Globe2, Users, Send, Lock, Sparkles,
} from "lucide-react";

/* ── Static catalogue ─────────────────────────────────────────────────── */
const SOURCES = [
  { id: "funnels",    label: "Our funnels", Icon: Globe2 },
  { id: "affiliates", label: "Affiliates",  Icon: Users },
  { id: "publishers", label: "Publishers",  Icon: Send },
];

const SECTORS = [
  {
    id: "mortgages", enquiry: "Mortgage enquiry", sector: "Mortgages", cpl: 38,
    pool: ["Northwind Capital", "Aspect Lending", "Beacon Finance", "Resolve Lending", "Pillar Mortgages", "Harbour Loans"],
  },
  {
    id: "life", enquiry: "Life insurance enquiry", sector: "Life Insurance", cpl: 34,
    pool: ["Pillar Life", "Harbour Cover", "Aspect Protect", "Nordic Mutual", "Tiger Cover", "Aegis Life"],
  },
  {
    id: "debt", enquiry: "Debt help enquiry", sector: "Debt Help", cpl: 28,
    pool: ["Resolve Debt", "Sterling Relief", "Liffey Solutions", "Harbour Counsel", "Aspect Debt"],
  },
  {
    id: "wealth", enquiry: "Pension & wealth enquiry", sector: "Wealth Management", cpl: 95,
    pool: ["Aegis Wealth", "Sterling Pensions", "Harbour Retirement", "Nordic Funds", "Liffey Wealth"],
  },
  {
    id: "business", enquiry: "Business finance enquiry", sector: "Business Finance", cpl: 52,
    pool: ["Aspect Credit", "Beacon Loans", "Resolve Credit", "Harbour Finance", "Sterling Loans"],
  },
];

const CYCLE = 6500;

function genScenario(sectorIdx = null, sourceIdx = null) {
  const s   = SECTORS[sectorIdx ?? Math.floor(Math.random() * SECTORS.length)];
  const src = SOURCES[sourceIdx ?? Math.floor(Math.random() * SOURCES.length)];
  const buyer = s.pool[Math.floor(Math.random() * s.pool.length)];
  return {
    source: src,
    enquiry: s.enquiry,
    sector: s.sector,
    cpl: s.cpl,
    matched_buyer: buyer,
    latency: 80 + Math.floor(Math.random() * 90),  // 80-170ms — always sub-second
  };
}

export default function ControlRoomTopology() {
  const [scenario, setScenario] = useState(() => genScenario(0, 0));
  const [phase, setPhase]       = useState("source"); // source → routing → matched → settle
  const [auto, setAuto]         = useState(true);
  const [runCount, setRunCount] = useState(0);
  const [sound, setSound]       = useState(false);
  const [hud, setHud]           = useState({ runs: 0, totalCpl: 0, fastestLatency: 999 });
  const timersRef     = useRef([]);
  const loopRef       = useRef(null);
  const audioCtxRef   = useRef(null);
  const lastPhaseRef  = useRef("source");

  /* Web Audio API — soft two-note "matched" ping. Sound is opt-in (autoplay policy). */
  const playMatchedTone = () => {
    if (!sound) return;
    try {
      if (!audioCtxRef.current) {
        const AC = window.AudioContext || window.webkitAudioContext;
        if (!AC) return;
        audioCtxRef.current = new AC();
      }
      const ctx = audioCtxRef.current;
      if (ctx.state === "suspended") ctx.resume();
      const now = ctx.currentTime;
      [880, 1318.5].forEach((freq, i) => {
        const osc  = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.type = "sine";
        osc.frequency.value = freq;
        gain.gain.value = 0;
        gain.gain.linearRampToValueAtTime(0.05, now + i * 0.05 + 0.01);
        gain.gain.exponentialRampToValueAtTime(0.0001, now + i * 0.05 + 0.22);
        osc.connect(gain).connect(ctx.destination);
        osc.start(now + i * 0.05);
        osc.stop(now + i * 0.05 + 0.25);
      });
    } catch (_) { /* sound is best-effort */ }
  };

  const clearTimers = () => {
    timersRef.current.forEach(clearTimeout);
    timersRef.current = [];
  };

  const runPhases = () => {
    clearTimers();
    setPhase("source");
    timersRef.current = [
      setTimeout(() => setPhase("routing"), 700),
      setTimeout(() => setPhase("matched"), 2200),
      setTimeout(() => setPhase("settle"),  3100),
    ];
  };

  /* Sound + HUD increment on each "matched" transition */
  useEffect(() => {
    if (phase === "matched" && lastPhaseRef.current !== "matched") {
      playMatchedTone();
      setHud((h) => ({
        runs: h.runs + 1,
        totalCpl: h.totalCpl + scenario.cpl,
        fastestLatency: Math.min(h.fastestLatency, scenario.latency),
      }));
    }
    lastPhaseRef.current = phase;
    // eslint-disable-next-line
  }, [phase]);

  useEffect(() => {
    runPhases();
    if (auto) {
      loopRef.current = setInterval(() => {
        setScenario(genScenario());
        setRunCount((c) => c + 1);
      }, CYCLE);
    }
    return () => {
      clearTimers();
      if (loopRef.current) clearInterval(loopRef.current);
    };
    // eslint-disable-next-line
  }, [auto, runCount]);

  const runNow = () => {
    if (loopRef.current) clearInterval(loopRef.current);
    setAuto(false);
    setScenario(genScenario());
    setRunCount((c) => c + 1);
  };

  const toggleAuto = () => setAuto((a) => !a);

  const s = scenario;
  const matched = phase === "matched" || phase === "settle";
  const SrcIcon = s.source.Icon;

  return (
    <div className="relative w-full rounded-2xl bg-white border border-slate-200 shadow-soft overflow-hidden" data-testid="control-room-topology">
      {/* HEADER */}
      <div className="px-6 py-4 flex items-center justify-between border-b border-slate-200 gap-4 flex-wrap">
        <div className="flex items-center gap-2.5">
          <span className="relative flex items-center justify-center size-2">
            <span className="absolute inline-flex h-full w-full rounded-full bg-[#22D3A6] opacity-75 animate-ping" />
            <span className="relative inline-flex rounded-full size-2 bg-[#22D3A6]" />
          </span>
          <span className="text-[11px] font-mono font-semibold tracking-[0.22em] text-slate-900">ROUTING · LIVE</span>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-1.5 text-[11px] font-mono tabular-nums text-slate-500">
            <Activity className="size-3 text-[#0055FF]" strokeWidth={2} />
            <span className="text-slate-900 font-semibold">{s.latency}ms</span>
            <span className="text-slate-400 hidden sm:inline">round-trip</span>
          </div>
          <button
            onClick={() => setSound((v) => !v)}
            className={`inline-flex items-center gap-1.5 text-[11px] font-mono uppercase tracking-[0.16em] transition-colors px-2 py-1 rounded-md hover:bg-slate-50 ${sound ? "text-[#0055FF]" : "text-slate-500 hover:text-[#0055FF]"}`}
            title={sound ? "Mute" : "Enable sound"}
            data-testid="sim-sound-toggle"
          >
            {sound ? <><Volume2 className="size-3" strokeWidth={2} /> Sound</> : <><VolumeX className="size-3" strokeWidth={2} /> Muted</>}
          </button>
          <button
            onClick={toggleAuto}
            className="inline-flex items-center gap-1.5 text-[11px] font-mono uppercase tracking-[0.16em] text-slate-500 hover:text-[#0055FF] transition-colors px-2 py-1 rounded-md hover:bg-slate-50"
            title={auto ? "Pause cycle" : "Resume cycle"}
            data-testid="sim-auto-toggle"
          >
            {auto ? <><Pause className="size-3" strokeWidth={2} /> Auto</> : <><Play className="size-3" strokeWidth={2} /> Paused</>}
          </button>
          <button
            onClick={runNow}
            className="inline-flex items-center gap-1.5 text-[11px] font-mono uppercase tracking-[0.16em] bg-[#0055FF] text-white px-3 py-1.5 rounded-full hover:bg-[#0044CC] transition-colors shadow-sm shadow-[#0055FF]/20"
            data-testid="sim-run-new"
          >
            <RotateCcw className="size-3" strokeWidth={2} /> Run new
          </button>
        </div>
      </div>

      {/* HUD strip — session stats */}
      {hud.runs > 0 && (
        <div className="px-6 py-2.5 border-b border-slate-200 bg-slate-50/60 grid grid-cols-3 gap-3 text-[11px] font-mono" data-testid="sim-hud">
          <div className="flex items-baseline gap-1.5">
            <span className="text-slate-500 uppercase tracking-[0.14em]">Routed</span>
            <span className="text-slate-900 tabular-nums font-semibold">{hud.runs} {hud.runs === 1 ? "lead" : "leads"}</span>
          </div>
          <div className="flex items-baseline gap-1.5">
            <span className="text-slate-500 uppercase tracking-[0.14em]">CPL</span>
            <span className="text-slate-900 tabular-nums font-semibold">£{hud.totalCpl.toLocaleString()}</span>
          </div>
          <div className="flex items-baseline gap-1.5 justify-end">
            <span className="text-slate-500 uppercase tracking-[0.14em]">Fastest</span>
            <span className="text-slate-900 tabular-nums font-semibold">{hud.fastestLatency}ms</span>
          </div>
        </div>
      )}

      {/* WORKFLOW — 3 main steps + 2 connectors */}
      <div className="relative px-6 sm:px-8 py-9 sm:py-12 overflow-x-auto">
        <div className="grid grid-cols-[auto_minmax(36px,1fr)_auto_minmax(36px,1fr)_auto] items-center gap-3 sm:gap-4 min-h-[200px] min-w-[560px] sm:min-w-0">

          {/* ── Step 1: SOURCE ────────────────────────────────────── */}
          <div className={`relative transition-all duration-500 ${phase === "source" ? "scale-[1.04]" : "scale-100"}`}>
            <div className="rounded-xl bg-[#0F172A] text-white px-3.5 py-3 shadow-md min-w-[138px]">
              <div className="text-[10px] uppercase tracking-[0.18em] text-white/50 font-mono font-semibold">Source</div>
              <div className="mt-1.5 flex items-center gap-2">
                <span className="grid place-items-center size-7 rounded-md bg-white/[0.08] ring-1 ring-white/15 text-[#7da9ff] flex-shrink-0">
                  <SrcIcon className="size-3.5" strokeWidth={1.9} />
                </span>
                <div className="leading-tight min-w-0">
                  <div className="text-[13px] font-medium tracking-tight whitespace-nowrap">{s.source.label}</div>
                  <div className="text-[10px] text-white/45 font-mono whitespace-nowrap">{s.enquiry}</div>
                </div>
              </div>
            </div>
            {phase === "source" && (
              <div className="absolute -inset-1 rounded-xl border border-[#0055FF]/35 animate-pulse pointer-events-none" />
            )}
          </div>

          <Connector active={phase !== "source"} tone="blue" />

          {/* ── Step 2: CONNEXIS ROUTING ENGINE (centrepiece) ────── */}
          <div className="relative">
            <div className={`relative rounded-xl border bg-gradient-to-b from-[#0a1124] to-[#0F172A] text-white px-4 py-3.5 shadow-lg transition-all duration-500 min-w-[178px]
              ${phase === "routing" ? "border-[#3d7bff] shadow-[0_0_28px_rgba(0,85,255,0.35)]" : "border-white/10"}`}
            >
              <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.16em] text-[#7da9ff] font-mono font-semibold whitespace-nowrap">
                <Sparkles className="size-3" strokeWidth={2} /> Routing engine
              </div>

              <div className="mt-2.5 flex items-center gap-2.5">
                {/* Connexis mark (mini, glowing when routing) */}
                <span className={`relative grid place-items-center size-10 rounded-lg bg-[#0a1124] ring-1 ring-white/15 flex-shrink-0
                  ${phase === "routing" ? "shadow-[0_0_18px_rgba(0,85,255,0.55)]" : ""}`}>
                  <svg viewBox="0 0 24 24" className="size-5" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M5 7h7M5 12h12M5 17h7" strokeLinecap="round" stroke="#ffffff" />
                    <circle cx="18" cy="7"  r="2.2" fill="#3d7bff" stroke="none" />
                    <circle cx="18" cy="17" r="2.2" fill="#3d7bff" stroke="none" />
                  </svg>
                  {phase === "routing" && (
                    <span className="absolute inset-0 rounded-lg border border-[#3d7bff] animate-[ringExpand_1.4s_ease-out_infinite]" />
                  )}
                </span>

                <div className="leading-tight min-w-0">
                  <div className="text-[9.5px] uppercase tracking-[0.16em] text-white/45 font-mono whitespace-nowrap">Sector detected</div>
                  <div className="text-[13.5px] font-semibold tracking-tight text-white truncate">{s.sector}</div>
                </div>
              </div>

              <div className="mt-2.5 pt-2.5 border-t border-white/[0.06] flex items-center justify-between text-[10px] font-mono text-white/55">
                <span className="inline-flex items-center gap-1"><Activity className="size-2.5 text-[#7da9ff]" strokeWidth={2.4} /> Routed</span>
                <span className="tabular-nums text-white">{s.latency}ms</span>
              </div>
            </div>

            <style>{`
              @keyframes ringExpand {
                0%   { opacity: 0.8; transform: scale(1); }
                100% { opacity: 0;   transform: scale(1.35); }
              }
            `}</style>
          </div>

          <Connector active={matched} tone="emerald" />

          {/* ── Step 3: MATCHED BUYER (single emerald card) ──────── */}
          <div className={`relative transition-all duration-500 ${matched ? "scale-[1.03]" : "scale-100 opacity-65"}`}>
            <div
              className={`relative rounded-xl px-3.5 py-3 min-w-[158px] transition-all duration-700
                ${matched
                  ? "bg-emerald-500/[0.10] border border-emerald-400/80 shadow-[0_0_20px_rgba(34,211,166,0.28)]"
                  : "bg-white border border-slate-200 shadow-sm"}`}
            >
              <div className="flex items-center gap-1.5">
                <Lock className={`size-3 ${matched ? "text-emerald-500" : "text-slate-400"}`} strokeWidth={2.4} />
                <span className={`text-[9.5px] uppercase tracking-[0.18em] font-mono font-semibold ${matched ? "text-emerald-600" : "text-slate-400"}`}>
                  Exclusive
                </span>
              </div>

              <div className={`mt-1.5 text-[13.5px] font-semibold tracking-tight truncate ${matched ? "text-slate-900" : "text-slate-500"}`}>
                {s.matched_buyer}
              </div>

              <div className="mt-1.5 flex items-baseline justify-between gap-2">
                <span className={`text-[10.5px] truncate ${matched ? "text-slate-500" : "text-slate-400"}`}>{s.sector}</span>
                <span className={`text-[15px] font-mono font-semibold tabular-nums flex-shrink-0 ${matched ? "text-emerald-600" : "text-slate-400"}`}>
                  £{s.cpl}
                </span>
              </div>
            </div>
          </div>

        </div>
      </div>

      {/* SUMMARY CARD */}
      <div className="px-8 sm:px-10 pb-8">
        <div
          className={`relative rounded-xl border bg-white px-6 py-5 transition-all duration-700
            ${matched
              ? "border-emerald-400/60 shadow-md shadow-emerald-500/10 opacity-100 translate-y-0"
              : "border-slate-200 opacity-65 translate-y-1"}`}
          data-testid="matched-summary-card"
        >
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-5 sm:gap-7">
            <div>
              <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.2em] text-slate-500 font-mono font-semibold">
                <Lock className="size-3 text-emerald-500" strokeWidth={2.2} /> Matched buyer
              </div>
              <div className="mt-2 text-[18px] font-semibold text-slate-900 tracking-tight truncate">{s.matched_buyer}</div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-mono font-semibold">Fixed CPL</div>
              <div className="mt-2 text-[22px] font-semibold text-emerald-600 tabular-nums tracking-tight">£{s.cpl}<span className="text-[12px] text-slate-400 ml-1 font-sans font-normal">/ lead</span></div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-mono font-semibold">Routed in</div>
              <div className="mt-2 text-[22px] font-semibold text-slate-900 tabular-nums tracking-tight">{s.latency}<span className="text-[14px] text-slate-500 ml-0.5">ms</span></div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * Connector — animated dashed line with a travelling glow dot when active.
 * Tone "blue" for source→engine, "emerald" for engine→buyer.
 */
function Connector({ active, tone = "blue" }) {
  const colour = tone === "emerald" ? "#22D3A6" : "#0055FF";
  const shadow = tone === "emerald" ? "rgba(34,211,166,0.55)" : "rgba(0,85,255,0.55)";
  return (
    <div className="relative h-px self-center w-full min-w-[20px]" data-testid="connector">
      <div className="absolute inset-0 border-t-2 border-dashed border-slate-200" />
      <div
        className="absolute inset-y-0 left-0 border-t-2 transition-[width] duration-700 ease-out"
        style={{ width: active ? "100%" : "0%", borderColor: colour }}
      />
      {active && (
        <span
          className="absolute -top-[3px] left-0 size-1.5 rounded-full"
          style={{
            background: colour,
            boxShadow: `0 0 8px 2px ${shadow}`,
            animation: "connectorDot 1.4s ease-in-out infinite",
          }}
        />
      )}
      <style>{`
        @keyframes connectorDot {
          0%   { left: 0%;  opacity: 0; }
          15%  { opacity: 1; }
          85%  { opacity: 1; }
          100% { left: 100%; opacity: 0; }
        }
      `}</style>
    </div>
  );
}
